import { useState, useEffect, useCallback } from 'react';
import { X, ExternalLink, ChevronLeft, ChevronRight, Search, PanelRightClose, PanelRight, Heart } from 'lucide-react';
import { allThemes } from '../data/styles';
import { getThemeCategory, getThemeSearchText } from '../data/themeCategories';
import { ProductLandingPage } from './ProductLandingPage';
import { DashboardPage } from './DashboardPage';
import { ViewportSwitcher, type Viewport, VIEWPORTS } from './ViewportSwitcher';
import { appShellColors } from '../styles/appShell';
import { getAccessibleAccentColor, getAccessibleTextColor, isDarkColor } from '../utils/color';
import { appCopy, getCategoryLabel, getDensityLabel, type Locale } from '../i18n';
import type { Theme } from '../types/theme';

type PageType = 'landing' | 'dashboard';

interface PreviewFrameProps {
  theme: Theme;
  themes?: Theme[];
  onClose?: () => void;
  pageType?: PageType;
  viewport?: Viewport;
  locale?: Locale;
  onViewportChange?: (viewport: Viewport) => void;
  onPageTypeChange?: (pageType: PageType) => void;
  onThemeChange?: (theme: Theme) => void;
  isShortlisted?: boolean;
  onToggleShortlist?: (theme: Theme) => void;
}

interface PageTypeToggleProps {
  value: PageType;
  locale: Locale;
  onChange: (pageType: PageType) => void;
}

function PageTypeToggle({ value, locale, onChange }: PageTypeToggleProps) {
  const copy = appCopy[locale];
  const options: Array<{ value: PageType; label: string }> = [
    { value: 'landing', label: copy.pageTypes.landing },
    { value: 'dashboard', label: copy.pageTypes.dashboard },
  ];

  return (
    <div
      className="grid min-h-11 w-[208px] flex-shrink-0 grid-cols-2 items-center gap-1 rounded-lg border p-1 sm:w-[228px]"
      style={{ backgroundColor: appShellColors.surface, borderColor: appShellColors.border }}
    >
      {options.map((option) => {
        const isActive = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={isActive}
            className="min-h-11 truncate rounded-md px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{
              backgroundColor: isActive ? appShellColors.primary : 'transparent',
              color: isActive ? appShellColors.onPrimary : appShellColors.textSecondary,
              ['--tw-ring-color' as string]: appShellColors.focusRing,
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toLowerCase();
  return tagName === 'input' || tagName === 'textarea' || target.isContentEditable;
}

export function PreviewFrame({
  theme,
  themes = allThemes,
  onClose,
  pageType = 'landing',
  viewport = 'desktop',
  locale = 'en',
  onViewportChange,
  onPageTypeChange,
  onThemeChange,
  isShortlisted = false,
  onToggleShortlist,
}: PreviewFrameProps) {
  const [localPageType, setLocalPageType] = useState<PageType>(pageType);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const copy = appCopy[locale];
  const currentPageType = onPageTypeChange ? pageType : localPageType;

  const currentIndex = themes.findIndex((candidateTheme) => candidateTheme.id === theme.id);

  const handlePrevTheme = useCallback(() => {
    if (currentIndex > 0 && onThemeChange) {
      onThemeChange(themes[currentIndex - 1]);
    }
  }, [currentIndex, onThemeChange, themes]);

  const handleNextTheme = useCallback(() => {
    if (currentIndex < themes.length - 1 && onThemeChange) {
      onThemeChange(themes[currentIndex + 1]);
    }
  }, [currentIndex, onThemeChange, themes]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target) && event.key !== 'Escape') {
        return;
      }

      if (event.key === 'ArrowLeft') {
        handlePrevTheme();
      } else if (event.key === 'ArrowRight') {
        handleNextTheme();
      } else if (event.key === 'Escape') {
        onClose?.();
      } else if ((event.key === 's' || event.key === 'S') && !sidebarOpen) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrevTheme, handleNextTheme, onClose, sidebarOpen]);

  const isDark = isDarkColor(theme.colors.background);
  const headerText = getAccessibleTextColor(theme.colors.textPrimary, theme.colors.background);
  const headerMuted = getAccessibleTextColor(theme.colors.textMuted, theme.colors.background, 3);
  const sourceLinkColor = getAccessibleAccentColor(theme.colors.textMuted, theme.colors.background, theme.colors.primary, 3);
  const viewportWidth = VIEWPORTS[viewport].width;
  const isMobileViewport = viewport === 'mobile';

  const handlePageTypeChange = (type: PageType) => {
    if (onPageTypeChange) {
      onPageTypeChange(type);
    } else {
      setLocalPageType(type);
    }
  };

  const filteredThemes = themes.filter((candidateTheme) => (
    getThemeSearchText(candidateTheme).includes(searchQuery.trim().toLowerCase())
  ));

  const handleSelectTheme = (selectedTheme: Theme) => {
    if (onThemeChange) {
      onThemeChange(selectedTheme);
    }
    setSidebarOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex overflow-hidden"
      style={{ backgroundColor: isDark ? '#111827' : appShellColors.background }}
    >
      {sidebarOpen && (
        <aside
          className="absolute inset-y-0 left-0 z-40 flex w-full max-w-80 flex-col border-r shadow-xl sm:relative sm:shadow-none"
          style={{ backgroundColor: appShellColors.surface, borderColor: appShellColors.border }}
          aria-label={copy.preview.switcher}
        >
          <div className="border-b p-4" style={{ borderColor: appShellColors.border }}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="font-semibold" style={{ color: appShellColors.textPrimary }}>
                {copy.preview.switcher}
              </h3>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                aria-label={copy.preview.closeSwitcher}
                className="flex h-11 w-11 items-center justify-center rounded-lg transition-colors hover:bg-gray-100"
                style={{ color: appShellColors.textPrimary }}
              >
                <PanelRightClose size={20} aria-hidden="true" />
              </button>
            </div>
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: appShellColors.textMuted }}
                aria-hidden="true"
              />
              <input
                type="search"
                aria-label={copy.preview.searchThemes}
                placeholder={copy.preview.searchPlaceholder}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="min-h-11 w-full rounded-lg border py-2 pl-9 pr-4 text-sm outline-none transition focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{
                  backgroundColor: appShellColors.surface,
                  borderColor: appShellColors.border,
                  color: appShellColors.textPrimary,
                }}
              />
            </div>
            <p className="mt-2 text-xs" style={{ color: appShellColors.textMuted }}>
              {copy.preview.keyboardHint}
            </p>
          </div>

          <div className="flex-1 overflow-auto">
            {filteredThemes.map((candidateTheme) => {
              const globalIndex = themes.findIndex((themeItem) => themeItem.id === candidateTheme.id);
              const isActive = candidateTheme.id === theme.id;

              return (
                <button
                  key={candidateTheme.id}
                  type="button"
                  onClick={() => handleSelectTheme(candidateTheme)}
                  aria-current={isActive ? 'true' : undefined}
                  className="flex min-h-16 w-full items-center gap-3 border px-3 py-3 text-left transition-colors hover:bg-gray-50"
                  style={{
                    backgroundColor: isActive ? '#eff6ff' : 'transparent',
                    borderColor: isActive ? '#bfdbfe' : 'transparent',
                  }}
                >
                  <div
                    className="h-10 w-10 flex-shrink-0 rounded-lg"
                    style={{ backgroundColor: candidateTheme.colors.background }}
                    aria-hidden="true"
                  >
                    <div
                      className="h-full w-full rounded-lg"
                      style={{
                        background: `linear-gradient(135deg, ${candidateTheme.colors.primary} 50%, ${candidateTheme.colors.background} 50%)`,
                      }}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium" style={{ color: appShellColors.textPrimary }}>
                        {candidateTheme.name}
                      </span>
                      {isActive && (
                        <span className="text-xs font-medium" style={{ color: appShellColors.focusRing }}>
                          {copy.preview.current}
                        </span>
                      )}
                    </div>
                    <p className="truncate text-xs" style={{ color: appShellColors.textMuted }}>
                      {getCategoryLabel(getThemeCategory(candidateTheme), locale)} / {getDensityLabel(candidateTheme.mood.density, locale)}
                    </p>
                  </div>
                  <span className="text-xs" style={{ color: appShellColors.textMuted }}>
                    {globalIndex + 1}/{themes.length}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className="flex-shrink-0 border-b px-4 py-3"
          style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border }}
        >
          <div className="space-y-3">
            <div className="grid min-w-0 gap-3 sm:grid-cols-[minmax(0,1fr)_172px_auto] sm:items-center">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={copy.preview.close}
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg transition-opacity hover:opacity-75"
                  style={{ backgroundColor: 'transparent', color: headerText }}
                >
                  <X size={20} aria-hidden="true" />
                </button>
                <div className="min-w-0">
                  <h2 className="truncate text-lg font-semibold" style={{ color: headerText }}>
                    {theme.name}
                  </h2>
                  <a
                    href={theme.sourceDesignMd}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-11 items-center gap-1 text-xs underline-offset-4 hover:underline"
                    style={{ color: sourceLinkColor }}
                  >
                    {copy.preview.source}
                    <ExternalLink size={12} aria-hidden="true" />
                  </a>
                </div>
              </div>

              <nav
                className="flex h-11 w-[172px] items-center justify-between justify-self-start sm:justify-self-center"
                aria-label={copy.preview.themeNavigation}
              >
                <button
                  type="button"
                  onClick={handlePrevTheme}
                  disabled={currentIndex <= 0}
                  className="flex h-11 w-11 items-center justify-center rounded-lg transition-colors disabled:opacity-30"
                  style={{ color: headerText }}
                  title={copy.preview.previousTheme}
                  aria-label={copy.preview.previousTheme}
                >
                  <ChevronLeft size={20} aria-hidden="true" />
                </button>
                <span className="w-16 text-center text-xs tabular-nums" style={{ color: headerMuted }}>
                  {currentIndex + 1} / {themes.length}
                </span>
                <button
                  type="button"
                  onClick={handleNextTheme}
                  disabled={currentIndex >= themes.length - 1}
                  className="flex h-11 w-11 items-center justify-center rounded-lg transition-colors disabled:opacity-30"
                  style={{ color: headerText }}
                  title={copy.preview.nextTheme}
                  aria-label={copy.preview.nextTheme}
                >
                  <ChevronRight size={20} aria-hidden="true" />
                </button>
              </nav>

              <div className="flex items-center gap-2 justify-self-start sm:justify-self-end">
                {onToggleShortlist && (
                  <button
                    type="button"
                    onClick={() => onToggleShortlist(theme)}
                    aria-label={isShortlisted ? copy.removeFromShortlist : copy.addToShortlist}
                    aria-pressed={isShortlisted}
                    className="flex h-11 w-11 items-center justify-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    style={{
                      backgroundColor: isShortlisted ? theme.colors.surface : 'transparent',
                      borderColor: isShortlisted ? theme.colors.primary : theme.colors.border,
                      color: headerText,
                      ['--tw-ring-color' as string]: theme.colors.primary,
                    }}
                    title={isShortlisted ? copy.removeFromShortlist : copy.addToShortlist}
                  >
                    <Heart size={20} fill={isShortlisted ? 'currentColor' : 'none'} aria-hidden="true" />
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  aria-label={copy.preview.toggleSwitcher}
                  aria-expanded={sidebarOpen}
                  className="flex h-11 w-11 items-center justify-center rounded-lg transition-colors"
                  style={{
                    backgroundColor: sidebarOpen ? theme.colors.surface : 'transparent',
                    color: headerText,
                  }}
                  title={copy.preview.toggleSwitcher}
                >
                  <PanelRight size={20} aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="-mx-4 flex min-w-0 overflow-x-auto px-4 sm:mx-0 sm:px-0">
              <div className="flex min-w-max items-center gap-3">
                <span
                  className="hidden max-w-28 flex-shrink-0 truncate rounded-full px-3 py-1 text-xs sm:inline"
                  style={{ backgroundColor: theme.colors.surface, color: getAccessibleTextColor(theme.colors.textMuted, theme.colors.surface, 3) }}
                >
                  {getDensityLabel(theme.mood.density, locale)}
                </span>

                <span
                  className="hidden max-w-36 flex-shrink-0 truncate rounded-full px-3 py-1 text-xs md:inline"
                  style={{ backgroundColor: theme.colors.surface, color: getAccessibleTextColor(theme.colors.textMuted, theme.colors.surface, 3) }}
                >
                  {getCategoryLabel(getThemeCategory(theme), locale)}
                </span>

                <PageTypeToggle value={currentPageType} locale={locale} onChange={handlePageTypeChange} />

                {onViewportChange && (
                  <ViewportSwitcher
                    current={viewport}
                    onChange={onViewportChange}
                    locale={locale}
                    theme={{
                      primary: theme.colors.primary,
                      background: theme.colors.background,
                      text: headerText,
                      surface: theme.colors.surface,
                      border: theme.colors.border,
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </header>

        <div
          className="flex flex-1 justify-center overflow-auto px-3 py-4 sm:py-6"
          style={{ backgroundColor: isDark ? '#111827' : appShellColors.background }}
        >
          <div
            className="transition-all duration-200"
            style={{
              width: typeof viewportWidth === 'number' ? `${viewportWidth}px` : viewportWidth,
              maxWidth: viewport === 'desktop' ? '100%' : 'min(100%, 375px)',
            }}
          >
            {isMobileViewport ? (
              <div
                className="mx-auto overflow-hidden"
                style={{
                  width: '100%',
                  maxWidth: '375px',
                  borderRadius: '40px',
                  border: '12px solid #1f2937',
                  boxShadow: '0 20px 60px rgba(15,23,42,0.28)',
                }}
              >
                <div
                  className="overflow-auto"
                  style={{ height: 'calc(100vh - 160px)', maxHeight: '700px', borderRadius: '28px' }}
                >
                  {currentPageType === 'landing' ? (
                    <ProductLandingPage theme={theme} locale={locale} isMobile={isMobileViewport} />
                  ) : (
                    <DashboardPage theme={theme} locale={locale} isMobile={isMobileViewport} />
                  )}
                </div>
              </div>
            ) : (
              <div
                className="overflow-hidden rounded-lg shadow-xl"
                style={{
                  backgroundColor: theme.colors.background,
                  boxShadow: isDark ? 'none' : '0 25px 50px -12px rgba(15,23,42,0.22)',
                }}
              >
                {currentPageType === 'landing' ? (
                  <ProductLandingPage theme={theme} locale={locale} />
                ) : (
                  <DashboardPage theme={theme} locale={locale} />
                )}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
